
import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';

interface CodeBlockProps {
  code: string | object;
  title?: string;
  language?: string;
  className?: string;
}

const CodeBlock = ({ code, title, language = "json", className }: CodeBlockProps) => {
  const [copied, setCopied] = useState(false);
  
  const formattedCode = typeof code === 'object' 
    ? JSON.stringify(code, null, 2) 
    : code;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(formattedCode);
    setCopied(true);
    toast("Copied to clipboard", {
      description: "The code has been copied to your clipboard"
    });
    
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <div className={cn(
      "relative rounded-md bg-recallio-dark-gray border border-recallio-light-gray/50",
      className
    )}>
      {title && (
        <div className="border-b border-recallio-light-gray/50 px-4 py-2 text-xs font-geist-mono flex items-center justify-between">
          <span>{title}</span>
        </div>
      )}
      
      <div className="relative">
        <button
          onClick={copyToClipboard}
          className="absolute top-2 right-2 p-1 rounded hover:bg-recallio-light-gray/30 transition-colors"
          aria-label="Copy code"
        >
          {copied ? <Check size={16} className="text-recallio-green" /> : <Copy size={16} />}
        </button>
        <pre className={cn(
          "p-4 overflow-x-auto font-geist-mono text-sm",
          title ? "pt-2" : "pt-4"
        )}>
          <code className={`language-${language}`}>{formattedCode}</code>
        </pre>
      </div>
    </div>
  );
};

export default CodeBlock;
